export function normalizePhone(value) {
  return String(value || '').replace(/\s+/g, '').trim()
}

export function getLoginCodeCountdownSeconds({
  resendAvailableAt = 0,
  now = Date.now()
} = {}) {
  const deadline = Number(resendAvailableAt) || 0
  const currentTime = Number(now) || 0
  if (!deadline || currentTime >= deadline) return 0
  return Math.ceil((deadline - currentTime) / 1000)
}

export function getValueId(value) {
  if (!value) return ''
  if (typeof value === 'string') return value.trim()
  return String(value._id || value.id || value.siteId || value.site_id || '').trim()
}

export function getSiteName(site) {
  if (!site) return ''
  return String(site.name || site.site_name || '').trim()
}

export function getSiteLabel(site) {
  const name = getSiteName(site)
  const code = String(site?.siteCode || site?.site_code || '').trim()
  return name || code || ''
}

export function normalizeSiteSummary(site = {}) {
  if (!site || typeof site !== 'object') return null

  const siteId = getValueId(site)
  const name = String(site.name || site.siteName || site.site_name || '').trim()
  const siteCode = String(site.siteCode || site.site_code || '').trim()
  const label = name || siteCode || ''
  if (!siteId && !label) {
    return null
  }

  return {
    _id: siteId || site._id || site.id || '',
    id: siteId || site._id || site.id || '',
    name: label,
    siteCode,
    address: String(site.address || site.location?.address || '').trim(),
    phone: String(site.phone || site.contactInfo?.phone || '').trim(),
    businessHours: site.businessHours || null,
    location: site.location || {},
    contactInfo: site.contactInfo || {}
  }
}

export function mergeRecentSiteCache(currentSite, recentSites = []) {
  const normalizedCurrent = currentSite ? normalizeSiteSummary(currentSite) : null
  const normalizedRecent = Array.isArray(recentSites) ? recentSites.filter(Boolean) : []
  const recent = normalizedRecent
    .map((item) => normalizeSiteSummary(item))
    .filter(Boolean)
  const currentId = getValueId(normalizedCurrent)
  const merged = []

  if (normalizedCurrent) {
    merged.push(normalizedCurrent)
  }

  recent.forEach((item) => {
    if (getValueId(item) !== currentId) {
      merged.push(item)
    }
  })

  return merged.slice(0, 5)
}

export function normalizeSitePreferences(payload = {}) {
  if (!payload || typeof payload !== 'object') {
    return {
      currentSite: null,
      recentSites: []
    }
  }

  const currentSite = normalizeSiteSummary(payload.currentSite || null)
  const recentSites = mergeRecentSiteCache(currentSite, Array.isArray(payload.recentSites) ? payload.recentSites : [])

  return {
    currentSite,
    recentSites
  }
}

export function normalizeCurrentAppointments(payload = {}) {
  const data = payload?.data && typeof payload.data === 'object' ? payload.data : payload
  const rawAppointments = Array.isArray(data?.appointments)
    ? data.appointments
    : Array.isArray(data?.current)
      ? data.current
      : null
  if (rawAppointments) {
    return rawAppointments.filter((item) => item && typeof item === 'object')
  }

  const singleAppointment = data?.appointment && typeof data.appointment === 'object'
    ? data.appointment
    : data
  if (singleAppointment && typeof singleAppointment === 'object' && (singleAppointment._id || singleAppointment.id)) {
    return [singleAppointment]
  }

  return []
}

export function getSiteAccentColor(value) {
  const text = String(value || '').replace(/\s+/g, '').trim()
  if (!text) return '#94a3b8'

  const palette = [
    '#2563eb',
    '#0f766e',
    '#ea580c',
    '#7c3aed',
    '#16a34a',
    '#db2777',
    '#0ea5e9',
    '#f59e0b'
  ]

  let hash = 0
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 33 + text.charCodeAt(index)) >>> 0
  }
  return palette[hash % palette.length]
}

export function formatDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

export function formatShortDate(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit'
  }).format(date)
}

export function formatDateTimeRangeText(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

export function normalizeAppointmentTimePreference(value) {
  const normalized = String(value == null ? '' : value).trim().toLowerCase()
  if (!normalized) return ''
  if (normalized === '10' || normalized === '10min' || normalized === '10m' || normalized === '10minute' || normalized === '10minutes') return '10min'
  if (normalized === '1' || normalized === '1hour' || normalized === '60min' || normalized === '60m') return '1hour'
  if (normalized === '2' || normalized === '2hour' || normalized === '120min' || normalized === '120m') return '2hour'
  if (normalized === '3' || normalized === 'halfhour' || normalized === '30min' || normalized === '30m') return 'halfhour'
  if (normalized === 'anytime') return 'today'
  return normalized
}

export function resolveAppointmentTimePreferenceLabel(value) {
  const normalized = normalizeAppointmentTimePreference(value)
  const labels = {
    '10min': '10分钟左右',
    '1hour': '1小时左右',
    '2hour': '2小时内',
    halfhour: '半小时内',
    today: '今日任意',
    morning: '上午',
    afternoon: '下午',
    evening: '晚上'
  }
  if (labels[normalized]) return labels[normalized]
  const rangeMatch = normalized.match(/^(\d{1,2})-(\d{1,2})$/)
  if (rangeMatch) {
    return `${Number(rangeMatch[1])}点-${Number(rangeMatch[2])}点`
  }
  return normalized || '时间段'
}

export function getQuickPreferenceMinutes(value) {
  const normalized = normalizeAppointmentTimePreference(value)
  if (normalized === '10min') return 10
  if (normalized === '1hour') return 60
  if (normalized === '2hour') return 120
  if (normalized === 'halfhour') return 30
  return null
}

export function calculateAppointmentTimeByPreference(selectedTime, now = new Date()) {
  const currentTime = now instanceof Date ? new Date(now.getTime()) : new Date(now)
  const normalized = normalizeAppointmentTimePreference(selectedTime)
  const quickMinutes = getQuickPreferenceMinutes(normalized)
  if (quickMinutes !== null) {
    return new Date(currentTime.getTime() + quickMinutes * 60 * 1000).toISOString()
  }

  if (normalized === 'today' || normalized === 'anytime') {
    const target = new Date(currentTime.getTime())
    target.setHours(18, 0, 0, 0)
    return target.toISOString()
  }

  if (normalized === 'morning') {
    const target = new Date(currentTime.getTime())
    target.setHours(12, 0, 0, 0)
    return target.toISOString()
  }

  if (normalized === 'afternoon') {
    const target = new Date(currentTime.getTime())
    target.setHours(18, 0, 0, 0)
    return target.toISOString()
  }

  if (normalized === 'evening') {
    const target = new Date(currentTime.getTime())
    target.setHours(22, 0, 0, 0)
    return target.toISOString()
  }

  const rangeMatch = normalized.match(/^(\d{2})-(\d{2})$/)
  if (rangeMatch) {
    const target = new Date(currentTime.getTime())
    target.setHours(Number(rangeMatch[2]), 0, 0, 0)
    if (target <= currentTime) {
      target.setDate(target.getDate() + 1)
    }
    return target.toISOString()
  }

  return currentTime.toISOString()
}

export function normalizeAppointmentBuilding(value) {
  const text = String(value || '').trim()
  const onlyDigits = text.replace(/\D/g, '')
  return onlyDigits.slice(0, 5)
}

export function normalizeAppointmentName(value) {
  const text = String(value || '').replace(/\s+/g, '')
  const normalized = text.replace(/[^\u4e00-\u9fa5A-Za-z0-9·]/g, '')
  return normalized.slice(0, 10)
}

export function normalizeRequiredAppointmentProfileField(field, value, fallback = '') {
  const normalize = field === 'building' ? normalizeAppointmentBuilding : normalizeAppointmentName
  return normalize(value) || normalize(fallback)
}

export function getProfileSaveState({ profile = {}, savedProfile = {} } = {}) {
  const normalizedProfile = {
    name: normalizeAppointmentName(profile.name || ''),
    building: normalizeAppointmentBuilding(profile.building || '')
  }
  const normalizedSavedProfile = {
    name: normalizeAppointmentName(savedProfile.name || ''),
    building: normalizeAppointmentBuilding(savedProfile.building || '')
  }
  const hasCompleteProfile = Boolean(normalizedProfile.name && normalizedProfile.building)
  const hasProfileChanges = normalizedProfile.name !== normalizedSavedProfile.name ||
    normalizedProfile.building !== normalizedSavedProfile.building

  return {
    normalizedProfile,
    hasCompleteProfile,
    hasProfileChanges,
    canSaveProfile: hasCompleteProfile && hasProfileChanges
  }
}

export function normalizeExpectedPackageCount(value, fallback = 1) {
  const numericValue = Number(value)
  if (Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 10) {
    return numericValue
  }
  const numericFallback = Number(fallback)
  if (Number.isInteger(numericFallback) && numericFallback >= 1 && numericFallback <= 10) {
    return numericFallback
  }
  return 1
}

export function getCurrentAppointmentCountLabel(appointment = {}) {
  const expectedCount = normalizeExpectedPackageCount(
    appointment?.expected_package_count ?? appointment?.expectedPackageCount ?? 1,
    1
  )
  const storedCount = normalizeExpectedPackageCount(
    appointment?.package_count ?? appointment?.packageCount ?? expectedCount,
    expectedCount
  )
  const normalizedStatus = String(appointment?.status || '').trim().toLowerCase()
  if (normalizedStatus === 'stored') {
    return `预约${expectedCount} 上架${storedCount}`
  }
  return `预约${expectedCount}`
}

export function getCurrentAppointmentCountLines(appointment = {}) {
  const expectedCount = normalizeExpectedPackageCount(
    appointment?.expected_package_count ?? appointment?.expectedPackageCount ?? 1,
    1
  )
  const storedCount = normalizeExpectedPackageCount(
    appointment?.package_count ?? appointment?.packageCount ?? expectedCount,
    expectedCount
  )
  const normalizedStatus = String(appointment?.status || '').trim().toLowerCase()
  if (normalizedStatus === 'stored') {
    return [`预约${expectedCount}`, `上架${storedCount}`]
  }
  return [`预约${expectedCount}`]
}

export function buildAppointmentSubmitPayload({ siteId, appointmentTime, appointmentForm = {} } = {}) {
  return {
    site_id: String(siteId || '').trim(),
    appointment_time: appointmentTime,
    appointment_method: 'phone',
    pickup_code_ori: '',
    recipient: normalizeAppointmentName(appointmentForm.name),
    building: normalizeAppointmentBuilding(appointmentForm.building),
    time_preference: normalizeAppointmentTimePreference(appointmentForm.selectedTime),
    expected_package_count: normalizeExpectedPackageCount(appointmentForm.expectedPackageCount, 1),
    accept_share: !!appointmentForm.acceptShare,
    message: String(appointmentForm.message || '').trim()
  }
}

export function resolveDefaultAppointmentForm(user = {}) {
  return {
    name: String(user.name || '').trim(),
    building: String(
      user.appointmentBuilding ||
      user.appointment_building ||
      user.building ||
      user.buildingNo ||
      user.building_no ||
      user.pickupBuilding ||
      user.pickup_building ||
      ''
    ).trim(),
    selectedTime: '',
    expectedPackageCount: 1,
    acceptShare: false,
    message: ''
  }
}

export function getProfileDisplayName(profile = {}, sessionUser = {}) {
  const savedName = String(profile.name || '').trim()
  if (savedName) return savedName
  return String(sessionUser.name || '').trim()
}

export function getTopbarGreetingLabel(profile = {}, sessionUser = {}) {
  const displayName = getProfileDisplayName(profile, sessionUser) || '微信用户'
  return `你好，${displayName}`
}

export function resolveInstallEntryState({
  isStandalone = false,
  hasNativePrompt = false
} = {}) {
  if (isStandalone) return 'installed'
  return hasNativePrompt ? 'native' : 'manual'
}

function resolveInstallHelpPlatform(normalizedUserAgent, normalizedPlatform, touchPoints) {
  if (/iphone|ipad|ipod/.test(normalizedUserAgent)) return 'ios'
  if (normalizedPlatform === 'macintel' && touchPoints > 1) return 'ios'
  if (normalizedUserAgent.includes('android')) return 'android'
  if (/macintosh|mac os x|macintel|macppc|mac68k/.test(normalizedPlatform) || /macintosh|mac os x/.test(normalizedUserAgent)) {
    return 'macos'
  }
  if (/win32|win64|windows|wince/.test(normalizedPlatform) || normalizedUserAgent.includes('windows')) return 'windows'
  if (/linux/.test(normalizedPlatform) || /linux|x11/.test(normalizedUserAgent)) return 'linux'
  return 'unknown'
}

function resolveInstallHelpBrowser(normalizedUserAgent) {
  if (/edgios|edga|edg\//.test(normalizedUserAgent)) return 'edge'
  if (/fxios|firefox\//.test(normalizedUserAgent)) return 'firefox'
  if (/samsungbrowser\//.test(normalizedUserAgent)) return 'samsung'
  if (/opr\/|opios|opera/.test(normalizedUserAgent)) return 'opera'
  if (/crios|chrome|chromium/.test(normalizedUserAgent)) return 'chrome'
  if (/safari/.test(normalizedUserAgent)) return 'safari'
  return 'unknown'
}

const INSTALL_HELP_BY_ENVIRONMENT = {
  'ios:safari': {
    title: '在 Safari 中添加到主屏幕',
    message: '先点底部中间的分享按钮，再在分享菜单里选择“添加到主屏幕”。',
    steps: ['点击底部中间的分享按钮', '在分享菜单中向上滑动找到“添加到主屏幕”', '确认名称后点“添加”']
  },
  'ios:chrome': {
    title: '在 Chrome 中添加到主屏幕',
    message: '先点地址栏右侧的分享按钮，再在系统分享菜单里选择“添加到主屏幕”。',
    steps: ['点击地址栏右侧的分享按钮', '在分享菜单中向上滑动找到“添加到主屏幕”', '确认名称后点“添加”']
  },
  'ios:edge': {
    title: '在 Edge 中添加到主屏幕',
    message: '先点底部右下角菜单，再进入共享菜单选择“添加到主屏幕”。',
    steps: ['点击底部右下角的菜单按钮', '在弹出的菜单中点击“共享”', '在系统共享菜单中找到“添加到主屏幕”', '确认名称后点“添加”']
  },
  'ios:firefox': {
    title: '在 Firefox 中添加到主屏幕',
    message: '先点底部右下角菜单，再进入分享菜单选择“添加到主屏幕”。',
    steps: ['点击底部右下角的菜单按钮', '在弹出的菜单中点“分享”', '在系统分享菜单中找到“添加到主屏幕”', '确认名称后点“添加”']
  },
  'ios:unknown': {
    title: '在当前浏览器中添加到主屏幕',
    message: 'iPhone 和 iPad 上通常需要先打开分享菜单，再选择“添加到主屏幕”。',
    steps: ['先找地址栏附近或底部工具栏里的“分享”按钮', '打开系统分享菜单', '找到“添加到主屏幕”并确认添加']
  },
  'android:chrome': {
    title: '在 Chrome 中安装应用',
    message: '优先留意地址栏里的安装图标；如果没有，再打开右上角菜单选择“安装应用”。',
    steps: ['查看地址栏右侧是否出现安装图标', '如果没有，点击右上角三点菜单', '选择“安装应用”或“添加到主屏幕”', '按提示确认安装']
  },
  'android:edge': {
    title: '在 Edge 中安装应用',
    message: '先点底部中间的菜单按钮，再寻找“安装应用”或“添加到手机”。',
    steps: ['点击底部中间的菜单按钮', '在弹出的菜单里左右滑动或展开更多操作', '选择“安装应用”或“添加到手机”', '按提示确认安装']
  },
  'android:firefox': {
    title: '在 Firefox 中添加到主屏幕',
    message: '打开浏览器菜单后，选择“安装”或进入“更多”后再点“添加到主屏幕”。',
    steps: ['点击右上角或底部工具栏中的菜单按钮', '优先查找“安装”', '如果没有“安装”，进入“更多”后点“添加到主屏幕”', '按提示确认添加']
  },
  'android:samsung': {
    title: '在 Samsung Internet 中安装应用',
    message: '打开底部菜单后，选择“添加页面到”并继续添加到主屏幕或安装应用。',
    steps: ['点击底部右下角的菜单按钮', '选择“添加页面到”', '选择“主屏幕”或“Apps 屏幕”', '按提示确认添加']
  },
  'android:unknown': {
    title: '在当前浏览器中安装应用',
    message: '请优先查找地址栏安装图标，或打开浏览器菜单寻找“安装应用”“添加到主屏幕”。',
    steps: ['查看地址栏周围是否有安装图标', '如果没有，打开浏览器菜单', '查找“安装应用”或“添加到主屏幕”', '按提示确认安装']
  },
  'macos:safari': {
    title: '在 Safari 中添加到 Dock',
    message: '点击工具栏中的分享按钮，然后选择“添加到 Dock”。',
    steps: ['点击 Safari 工具栏中的分享按钮', '选择“添加到 Dock”', '确认名称后点击“添加”']
  },
  'windows:chrome': {
    title: '在 Chrome 中安装应用',
    message: '优先点击地址栏右侧的安装图标；如果没有，再打开右上角菜单选择“安装”。',
    steps: ['查看地址栏右侧是否出现安装图标', '如果没有，点击右上角三点菜单', '选择“安装 驿站预约...”或“安装此网站为应用”', '按提示确认安装']
  },
  'windows:edge': {
    title: '在 Edge 中安装应用',
    message: '优先点击地址栏右侧的应用安装图标；如果没有，再从右上角菜单进入“应用”。',
    steps: ['查看地址栏右侧是否出现应用安装图标', '如果没有，点击右上角三点菜单', '进入“应用”后选择“将此站点安装为应用”', '按提示确认安装']
  },
  'macos:chrome': {
    title: '在 Chrome 中安装应用',
    message: '优先点击地址栏右侧的安装图标；如果没有，再打开右上角菜单选择“安装”。',
    steps: ['查看地址栏右侧是否出现安装图标', '如果没有，点击右上角三点菜单', '选择“安装 驿站预约...”或“安装此网站为应用”', '按提示确认安装']
  },
  'macos:edge': {
    title: '在 Edge 中安装应用',
    message: '优先点击地址栏右侧的应用安装图标；如果没有，再从右上角菜单进入“应用”。',
    steps: ['查看地址栏右侧是否出现应用安装图标', '如果没有，点击右上角三点菜单', '进入“应用”后选择“将此站点安装为应用”', '按提示确认安装']
  },
  'linux:chrome': {
    title: '在 Chrome 中安装应用',
    message: '优先点击地址栏右侧的安装图标；如果没有，再打开右上角菜单选择“安装”。',
    steps: ['查看地址栏右侧是否出现安装图标', '如果没有，点击右上角三点菜单', '选择“安装 驿站预约...”或“安装此网站为应用”', '按提示确认安装']
  },
  'linux:edge': {
    title: '在 Edge 中安装应用',
    message: '优先点击地址栏右侧的应用安装图标；如果没有，再从右上角菜单进入“应用”。',
    steps: ['查看地址栏右侧是否出现应用安装图标', '如果没有，点击右上角三点菜单', '进入“应用”后选择“将此站点安装为应用”', '按提示确认安装']
  }
}

const INSTALL_HELP_FIREFOX_DESKTOP = {
  title: '当前浏览器不支持安装',
  message: 'Firefox 桌面版目前不支持把这个站点安装成桌面应用，请改用 Chrome 或 Edge。',
  steps: ['复制当前页面链接', '使用 Chrome 或 Edge 打开', '点击地址栏右侧安装图标，或从浏览器菜单中执行安装']
}

const DEFAULT_INSTALL_HELP = {
  title: '安装此应用',
  message: '当前浏览器的安装入口可能不同，请优先查找地址栏安装图标或浏览器菜单中的“安装应用”“添加到主屏幕”。',
  steps: ['查看地址栏周围是否有安装图标', '如果没有，打开浏览器菜单', '查找“安装应用”或“添加到主屏幕”', '如仍找不到，请改用 Safari、Chrome 或 Edge']
}

export function resolveInstallHelp({
  userAgent = '',
  platform = '',
  maxTouchPoints = 0
} = {}) {
  const normalizedUserAgent = String(userAgent || '').toLowerCase()
  const normalizedPlatform = String(platform || '').toLowerCase()
  const touchPoints = Number(maxTouchPoints) || 0
  const resolvedPlatform = resolveInstallHelpPlatform(normalizedUserAgent, normalizedPlatform, touchPoints)
  const resolvedBrowser = resolveInstallHelpBrowser(normalizedUserAgent)
  const environmentKey = `${resolvedPlatform}:${resolvedBrowser}`
  const platformFallbackKey = `${resolvedPlatform}:unknown`

  if (resolvedBrowser === 'firefox' && !['ios', 'android'].includes(resolvedPlatform)) {
    return INSTALL_HELP_FIREFOX_DESKTOP
  }

  return INSTALL_HELP_BY_ENVIRONMENT[environmentKey] ||
    INSTALL_HELP_BY_ENVIRONMENT[platformFallbackKey] ||
    DEFAULT_INSTALL_HELP
}

export function resolveProfileFromBoot({
  authenticated = false,
  cachedProfile = null,
  sessionUser = {}
} = {}) {
  const sessionProfile = {
    name: normalizeAppointmentName(sessionUser.name || ''),
    building: normalizeAppointmentBuilding(
      sessionUser.appointmentBuilding ||
      sessionUser.appointment_building ||
      sessionUser.building ||
      sessionUser.buildingNo ||
      sessionUser.building_no ||
      ''
    )
  }

  if (authenticated) {
    return sessionProfile
  }

  return {
    name: normalizeAppointmentName(cachedProfile?.name || sessionProfile.name || ''),
    building: normalizeAppointmentBuilding(cachedProfile?.building || sessionProfile.building || '')
  }
}

export function shouldThrottleStationSync({
  siteId = '',
  lastSyncedSiteId = '',
  lastSyncedAt = 0,
  now = Date.now(),
  ttlMs = 60 * 1000
} = {}) {
  const normalizedSiteId = String(siteId || '').trim()
  const normalizedLastSyncedSiteId = String(lastSyncedSiteId || '').trim()
  const numericLastSyncedAt = Number(lastSyncedAt)
  const numericNow = Number(now)
  const numericTtlMs = Number(ttlMs)

  if (!normalizedSiteId || normalizedSiteId !== normalizedLastSyncedSiteId) {
    return false
  }

  if (!Number.isFinite(numericLastSyncedAt) || !Number.isFinite(numericNow) || !Number.isFinite(numericTtlMs) || numericTtlMs <= 0) {
    return false
  }

  return numericNow - numericLastSyncedAt < numericTtlMs
}

export function shouldClearSessionForBootError(error) {
  const status = Number(error?.status || 0)
  return status === 401 || status === 403
}

export function shouldDisableAppointmentEventStream(error) {
  const status = Number(error?.status || 0)
  return status === 404 || status === 410 || status === 501
}

export function normalizeAssistantMessages(messages = []) {
  if (!Array.isArray(messages)) return []

  return messages.map((message, index) => {
    const content = message?.content && typeof message.content === 'object'
      ? message.content
      : { type: 'text', text: message?.text || '' }
    const id = String(message?._id || message?.id || message?.clientMessageId || `assistant-message-${index}`).trim()
    
    let text = String(content.text || message?.text || '').trim()
    if (text.includes('有什么我能帮你的吗') || text.includes('我要预约取件')) {
      text = '有什么可以帮你的吗？'
    }

    return {
      id,
      role: String(message?.role || 'assistant').trim() || 'assistant',
      text,
      contentType: String(content.type || 'text').trim() || 'text',
      cards: Array.isArray(content.cards) ? content.cards : [],
      actions: Array.isArray(content.actions) ? content.actions : [],
      createdAt: String(message?.created_at || message?.createdAt || '').trim(),
      pending: message?.pending === true,
      error: String(message?.error || '').trim()
    }
  })
}

function createAssistantSiteEntryCard(overrides = {}) {
  return {
    type: 'site_entry_card',
    reason: String(overrides.reason || 'no_site_found').trim() || 'no_site_found',
    title: String(overrides.title || '输入站点').trim() || '输入站点',
    description: String(overrides.description || '你还没有可用的站点，请先扫码或输入站点ID，我帮你查询站点信息。').trim() || '你还没有可用的站点，请先扫码或输入站点ID，我帮你查询站点信息。',
    inputLabel: String(overrides.inputLabel || '站点码').trim() || '站点码',
    placeholder: String(overrides.placeholder || '请输入站点码').trim() || '请输入站点码',
    scanLabel: String(overrides.scanLabel || '扫码').trim() || '扫码',
    submitLabel: String(overrides.submitLabel || '确认').trim() || '确认',
    successHint: String(overrides.successHint || '已切换站点，请继续确认预约流程。').trim() || '已切换站点，请继续确认预约流程。'
  }
}

function shouldAddAssistantSiteEntryFallback(message = {}) {
  if (!message || typeof message !== 'object') return false
  if (String(message.role || '').trim() !== 'assistant') return false

  const text = String(message.text || '').trim()
  if (!text) return false

  return [
    /没有查找到任何站点/,
    /未找到驿站/,
    /请先扫码或输入站点/
  ].some((pattern) => pattern.test(text))
}

export function withAssistantSiteEntryFallback(message = {}) {
  const nextMessage = message && typeof message === 'object' ? { ...message } : {}
  const cards = Array.isArray(nextMessage.cards) ? nextMessage.cards : []
  if (cards.some((card) => String(card?.type || '').trim() === 'site_entry_card')) {
    nextMessage.cards = cards
    return nextMessage
  }

  if (!shouldAddAssistantSiteEntryFallback(nextMessage)) {
    nextMessage.cards = cards
    return nextMessage
  }

  nextMessage.cards = [
    ...cards,
    createAssistantSiteEntryCard({ reason: 'no_site_found' })
  ]
  return nextMessage
}

export function normalizeAssistantSiteSlotPickerCard(card = {}) {
  const source = card && typeof card === 'object' ? card : {}
  const sites = Array.isArray(source.sites) ? source.sites : []
  const normalizedSites = sites.map((item) => {
    const site = item && typeof item === 'object' ? item : {}
    const siteId = String(site.siteId || getValueId(site.site) || '').trim()
    const siteLabel = String(site.siteLabel || getSiteLabel(site.site) || siteId).trim()
    const slots = (Array.isArray(site.slots) ? site.slots : [])
      .filter((slot) => slot && typeof slot === 'object' && slot.available !== false)
      .map((slot) => ({
        value: String(slot.value || '').trim(),
        label: String(slot.label || slot.value || '').trim(),
        type: String(slot.type || '').trim(),
        current: Number(slot.current || 0),
        max: Number(slot.max || 0)
      }))
      .filter((slot) => slot.value)

    return {
      siteId,
      siteLabel,
      site: site.site || null,
      slots
    }
  }).filter((site) => site.siteId && site.slots.length)

  return {
    type: 'site_slot_picker',
    title: String(source.title || '选择驿站和预约时段').trim(),
    sites: normalizedSites
  }
}

export function getAssistantAppointmentStatusSignature(appointment = null) {
  if (!appointment || typeof appointment !== 'object') return ''
  return [
    getValueId(appointment),
    String(appointment.status || '').trim(),
    appointment.is_invalid === true ? 'invalid' : '',
    String(appointment.pickup_info?.method || '').trim(),
    String(appointment.outbound_info?.marked_at || '').trim()
  ].join('|')
}

export function shouldNotifyAssistantAppointmentStatusChange(previousAppointment = null, nextAppointment = null) {
  const previousSignature = getAssistantAppointmentStatusSignature(previousAppointment)
  const nextSignature = getAssistantAppointmentStatusSignature(nextAppointment)
  if (!previousSignature || !nextSignature) return false
  return previousSignature !== nextSignature
}

export function getAssistantAppointmentPollIntervalMs({
  stableRounds = 0,
  changed = false
} = {}) {
  if (changed) return 20 * 1000

  const normalizedStableRounds = Math.max(0, Number(stableRounds) || 0)
  if (normalizedStableRounds >= 6) return 60 * 1000
  if (normalizedStableRounds >= 3) return 40 * 1000
  return 20 * 1000
}

function getCappedExponentialDelayMs({
  failedAttempts = 0,
  baseDelayMs = 5_000,
  maxDelayMs = 60_000
} = {}) {
  const normalizedAttempts = Math.max(0, Number(failedAttempts) || 0)
  const normalizedBaseDelayMs = Math.max(1, Number(baseDelayMs) || 1)
  const normalizedMaxDelayMs = Math.max(normalizedBaseDelayMs, Number(maxDelayMs) || normalizedBaseDelayMs)
  return Math.min(normalizedMaxDelayMs, normalizedBaseDelayMs * (2 ** normalizedAttempts))
}

export function getAssistantConversationRetryDelayMs({ failedAttempts = 0 } = {}) {
  return getCappedExponentialDelayMs({
    failedAttempts,
    baseDelayMs: 10_000,
    maxDelayMs: 60_000
  })
}

export function resolveAssistantVoiceGesture({ startY, currentY, cancelDistance = 48 } = {}) {
  const start = Number(startY)
  const current = Number(currentY)
  const threshold = Number(cancelDistance)
  if (!Number.isFinite(start) || !Number.isFinite(current) || !Number.isFinite(threshold)) return false
  return start - current >= Math.max(0, threshold)
}

export function getAssistantVoicePadLabel({ listening = false, draft = '', cancelling = false } = {}) {
  if (cancelling) return '松手取消'
  if (!listening) return String(draft || '').trim() || '按住说话'
  return String(draft || '').trim() || '松开发送，上滑取消'
}

export function getAppointmentEventReconnectDelayMs({ failedAttempts = 0 } = {}) {
  return getCappedExponentialDelayMs({
    failedAttempts,
    baseDelayMs: 5_000,
    maxDelayMs: 60_000
  })
}

export function mergeIncrementalAppointmentHistory(existingHistory = [], changedHistory = []) {
  const currentItems = Array.isArray(existingHistory) ? existingHistory : []
  const changedItems = Array.isArray(changedHistory) ? changedHistory.filter(Boolean) : []
  if (!changedItems.length) return currentItems

  const changedIds = new Set(
    changedItems
      .map((item) => String(item?._id || item?.id || '').trim())
      .filter(Boolean)
  )

  return [
    ...changedItems,
    ...currentItems.filter((item) => !changedIds.has(String(item?._id || item?.id || '').trim()))
  ]
}

export function shouldRefreshAppointmentHistory({
  historyLoadedAt = 0,
  historyNeedsRefresh = false,
  now = Date.now(),
  staleAfterMs = 60 * 1000
} = {}) {
  if (historyNeedsRefresh) return true

  const loadedAt = Number(historyLoadedAt)
  const currentNow = Number(now)
  const staleMs = Number(staleAfterMs)

  if (!Number.isFinite(loadedAt) || loadedAt <= 0) return true
  if (!Number.isFinite(currentNow) || !Number.isFinite(staleMs) || staleMs <= 0) return false

  return currentNow - loadedAt >= staleMs
}

export function formatAppointmentCode(appointment) {
  return String(
    appointment?.pickup_code_ori ||
    appointment?.pickup_code ||
    appointment?.pickupCodeOri ||
    appointment?.pickupCode ||
    appointment?.package_info?.pickup_code ||
    appointment?.package_info?.pickupCode ||
    appointment?.package_info?.code ||
    appointment?.pickup_info?.pickup_code ||
    appointment?.pickup_info?.pickupCode ||
    appointment?.pickup_info?.code ||
    ''
  ).trim()
}

export function parseManualShelfModelCode(modelCode = '') {
  const normalizedModelCode = String(modelCode || '').trim()
  if (!normalizedModelCode) return null

  const matched = normalizedModelCode.match(/^MANUAL-SHELF-R(\d+)C(\d+)-/i)
  if (!matched) return null

  const rows = Number(matched[1])
  const cols = Number(matched[2])
  if (!Number.isInteger(rows) || !Number.isInteger(cols) || rows <= 0 || cols <= 0) {
    return null
  }

  return {
    rows,
    cols,
    cellCount: rows * cols
  }
}

function parseAppointmentLayout(layout = null) {
  if (!layout) return null

  if (typeof layout === 'string') {
    const matched = String(layout).trim().match(/^R?(\d+)\s*[xX*]\s*C?(\d+)$/)
    if (matched) {
      const rows = Number(matched[1])
      const cols = Number(matched[2])
      if (Number.isInteger(rows) && Number.isInteger(cols) && rows > 0 && cols > 0) {
        return {
          rows,
          cols,
          cellCount: rows * cols
        }
      }
    }
    return null
  }

  if (typeof layout !== 'object') return null

  const rows = Number(
    layout.rows ??
    layout.row ??
    layout.rowCount ??
    layout.row_count ??
    0
  )
  const cols = Number(
    layout.cols ??
    layout.columns ??
    layout.col ??
    layout.column ??
    layout.columnCount ??
    layout.column_count ??
    layout.colCount ??
    layout.col_count ??
    0
  )

  if (!Number.isInteger(rows) || !Number.isInteger(cols) || rows <= 0 || cols <= 0) {
    return null
  }

  return {
    rows,
    cols,
    cellCount: rows * cols
  }
}

function toAlphabeticRowLabel(index = 0) {
  let value = Math.max(0, Number(index) || 0)
  let label = ''

  do {
    label = String.fromCharCode(65 + (value % 26)) + label
    value = Math.floor(value / 26) - 1
  } while (value >= 0)

  return label
}

export function resolveAppointmentLocationPreviewGrid(appointment = {}) {
  const modelCode = String(
    appointment?.cabinet_info?.device_model ||
    appointment?.cabinet_info?.model_code ||
    appointment?.cabinet_info?.modelCode ||
    appointment?.cabinet_id?.device_model ||
    appointment?.cabinet_id?.model_code ||
    appointment?.cabinet_id?.modelCode ||
    appointment?.pickup_info?.storage?.device_model ||
    appointment?.pickup_info?.storage?.model_code ||
    appointment?.pickup_info?.storage?.modelCode ||
    appointment?.outbound_info?.storage?.device_model ||
    appointment?.outbound_info?.storage?.model_code ||
    appointment?.outbound_info?.storage?.modelCode ||
    ''
  ).trim()

  const parsed = (
    parseManualShelfModelCode(modelCode) ||
    parseAppointmentLayout(appointment?.cabinet_info?.layout) ||
    parseAppointmentLayout(appointment?.cabinet_id?.layout) ||
    parseAppointmentLayout(appointment?.pickup_info?.storage?.layout) ||
    parseAppointmentLayout(appointment?.outbound_info?.storage?.layout)
  )
  if (!parsed) {
    return {
      modelCode,
      rows: 0,
      cols: 0,
      cellCount: 0,
      cells: []
    }
  }

  return {
    modelCode,
    rows: parsed.rows,
    cols: parsed.cols,
    cellCount: parsed.cellCount,
    cells: Array.from({ length: parsed.cellCount }, (_, index) => index + 1)
  }
}

export function getAppointmentLocationCellVisualLabel({
  cellNumber = 0,
  cols = 0
} = {}) {
  const normalizedCellNumber = Number(cellNumber)
  const normalizedCols = Number(cols)
  if (!Number.isInteger(normalizedCellNumber) || normalizedCellNumber < 1) return ''
  if (!Number.isInteger(normalizedCols) || normalizedCols < 1) return String(normalizedCellNumber)

  const rowIndex = Math.floor((normalizedCellNumber - 1) / normalizedCols)
  const colNumber = ((normalizedCellNumber - 1) % normalizedCols) + 1
  return `${toAlphabeticRowLabel(rowIndex)}${colNumber}`
}

function normalizeAppointmentCompartmentValue(value) {
  if (value && typeof value === 'object') {
    return String(
      value.number ??
      value.label ??
      value.id ??
      value._id ??
      ''
    ).trim()
  }

  return String(value || '').trim()
}

function resolveAppointmentCellNumberFromValue(rawValue, cols = 0, maxCellCount = 0) {
  const normalized = normalizeAppointmentCompartmentValue(rawValue)
  if (!normalized) return 0

  if (/^[A-Za-z]+\d+$/.test(normalized) && cols > 0) {
    const match = normalized.match(/^([A-Za-z]+)(\d+)$/)
    if (match) {
      const letters = match[1].toUpperCase()
      const column = Number(match[2])
      let rowIndex = 0
      for (let index = 0; index < letters.length; index += 1) {
        rowIndex = rowIndex * 26 + (letters.charCodeAt(index) - 64)
      }
      const cellNumber = (rowIndex - 1) * cols + column
      if (Number.isInteger(cellNumber) && cellNumber >= 1 && (!maxCellCount || cellNumber <= maxCellCount)) {
        return cellNumber
      }
    }
  }

  const numericValue = Number(normalized)
  if (!Number.isInteger(numericValue) || numericValue < 1) return 0
  if (maxCellCount > 0 && numericValue > maxCellCount) return 0
  return numericValue
}

export function normalizeAppointmentStorageAllocations(appointment = {}) {
  const source = Array.isArray(appointment?.storage_allocations)
    ? appointment.storage_allocations
    : Array.isArray(appointment?.storageAllocations)
      ? appointment.storageAllocations
      : []

  const rootTrackingNumber = String(
    appointment?.tracking_number ??
    appointment?.trackingNumber ??
    appointment?.pickup_code_ori ??
    appointment?.pickupCodeOri ??
    appointment?.pickup_code ??
    appointment?.pickupCode ??
    ''
  ).trim()

  const allocations = source
    .map((item) => (item && typeof item === 'object' ? item : {}))
    .map((item) => {
      const itemTracking = String(item.tracking_number ?? item.trackingNumber ?? '').trim()
      const trackingNumber = itemTracking || rootTrackingNumber
      return {
        cabinetId: String(item.cabinet_id || item.cabinetId || appointment?.cabinet_id?._id || appointment?.cabinet_id || '').trim(),
        compartmentId: normalizeAppointmentCompartmentValue(item.compartment_id ?? item.compartmentId),
        compartmentLabel: String(
          item.compartment_label ||
          item.compartmentLabel ||
          item.compartment_id ||
          item.compartmentId ||
          ''
        ).trim(),
        packageCount: Number(item.package_count ?? item.packageCount ?? 1) || 1,
        trackingNumber
      }
    })
    .filter((item) => item.compartmentId || item.compartmentLabel)

  if (allocations.length > 0) {
    return allocations
  }

  const fallbackCompartmentValue = (
    appointment?.compartmentLabel ??
    appointment?.compartment_label ??
    appointment?.pickup_info?.storage?.compartmentLabel ??
    appointment?.pickup_info?.compartmentLabel ??
    appointment?.outbound_info?.storage?.compartmentLabel ??
    appointment?.outbound_info?.compartmentLabel ??
    appointment?.compartment_id
  )
  const fallbackLabel = normalizeAppointmentCompartmentValue(fallbackCompartmentValue)
  if (!fallbackLabel) {
    return []
  }

  return [{
    cabinetId: String(appointment?.cabinet_id?._id || appointment?.cabinet_id || '').trim(),
    compartmentId: normalizeAppointmentCompartmentValue(appointment?.compartment_id || fallbackLabel),
    compartmentLabel: fallbackLabel,
    packageCount: Number(appointment?.package_count ?? appointment?.packageCount ?? 1) || 1,
    trackingNumber: rootTrackingNumber
  }]
}

export function resolveAppointmentLocationSummary(appointment = {}) {
  const allocations = normalizeAppointmentStorageAllocations(appointment)
  if (!allocations.length) return ''
  return allocations
    .map((item) => item.compartmentLabel || item.compartmentId)
    .filter(Boolean)
    .join('、')
}

export function resolveAppointmentLocationCellNumbers(appointment = {}, { cellCount = 0, cols = 0 } = {}) {
  const allocations = normalizeAppointmentStorageAllocations(appointment)
  const numbers = allocations
    .map((item) => resolveAppointmentCellNumberFromValue(item.compartmentLabel || item.compartmentId, Number(cols) || 0, Number(cellCount) || 0))
    .filter((item) => Number.isInteger(item) && item > 0)

  return [...new Set(numbers)]
}

export function getAppointmentStatusLabel(status) {
  const normalized = String(status || '').trim()
  const map = {
    pending: '待处理',
    'on-hold': '待确认',
    stored: '已入库',
    expired: '已过期',
    completed: '已完成',
    cancelled: '已取消',
    rejected: '已驳回',
    invalid: '已失效'
  }
  return map[normalized] || normalized || '预约'
}

function normalizePhoneTailDigits(value = '') {
  return String(value || '').replace(/\D/g, '').slice(-4)
}

export function resolveCurrentAppointmentCardDisplay(appointment = {}, { phoneTail = '' } = {}) {
  const status = String(appointment?.status || '').trim()
  const pickupDisplayMode = String(appointment?.pickup_display_mode || '').trim()
  const pickupCode = formatAppointmentCode(appointment)
  const tailDigits = normalizePhoneTailDigits(
    phoneTail ||
    appointment?.phone_tail ||
    appointment?.phone ||
    appointment?.user_id?.phone
  )
  let pickupLabel = pickupDisplayMode === 'pickup_code' ? '取件码' : '手机尾号'
  let pickupValue = pickupDisplayMode === 'pickup_code' ? pickupCode : tailDigits

  if (!pickupValue && pickupDisplayMode === 'pickup_code' && tailDigits) {
    pickupLabel = '手机尾号'
    pickupValue = tailDigits
  }

  if (!pickupValue && pickupDisplayMode !== 'pickup_code' && pickupCode) {
    pickupLabel = '取件码'
    pickupValue = pickupCode
  }

  if (status === 'pending') {
    return {
      statusLabel: '',
      pickupLabel: '',
      pickupValue: '待处理'
    }
  }

  return {
    statusLabel: getAppointmentStatusLabel(status),
    pickupLabel,
    pickupValue: pickupValue || '暂无'
  }
}

export function isCurrentAppointmentActionable(appointment) {
  const status = String(appointment?.status || '').trim()
  if (!status) return false
  return ['pending', 'on-hold', 'stored', 'expired', 'completed'].includes(status)
}

export function getBlockingCurrentAppointment(appointments = [], siteId = null) {
  if (!Array.isArray(appointments)) return null
  return appointments.find((appointment) => {
    const status = String(appointment?.status || '').trim()
    const isBlocking = ['pending', 'on-hold', 'stored'].includes(status)
    if (!isBlocking) return false
    if (siteId) {
      const apptSiteId = getValueId(appointment?.site_id) || getValueId(appointment?.site)
      return apptSiteId === getValueId(siteId)
    }
    return true
  }) || null
}

export function getAppointmentActionKind(appointment) {
  if (!appointment) return ''
  if (appointment.is_invalid) return 'dismiss-invalid'
  if (appointment.status === 'expired') return 'dismiss-expired'
  if (appointment.status === 'completed' && appointment.pickup_info?.method === 'admin_force') {
    return 'dismiss-eviction'
  }
  if (appointment.status === 'pending' || appointment.status === 'on-hold') {
    return 'cancel'
  }
  return ''
}

const NOTIFICATION_ACTION_KINDS = new Set(['dismiss-invalid', 'dismiss-expired', 'dismiss-eviction'])

export function getNotificationAppointments(appointments = []) {
  if (!Array.isArray(appointments)) return []
  return appointments.filter((item) => {
    if (!item || typeof item !== 'object') return false
    return NOTIFICATION_ACTION_KINDS.has(getAppointmentActionKind(item))
  })
}

export function getNotificationAppointmentMessage(actionKind) {
  if (actionKind === 'dismiss-invalid') return '该预约已被管理员标记为无效'
  if (actionKind === 'dismiss-expired') return '该预约已过期'
  if (actionKind === 'dismiss-eviction') return '该预约已被管理员清退'
  return ''
}
